import {ActionReducerMap, createFeatureSelector, createSelector} from '@ngrx/store';
import * as fromRoot from '../../reducers';
import {QuoteState} from '../model/quote';
import {QuoteBarItem} from '../model/quote-bar-item';
import * as fromCountries from './countries.reducer';
import * as fromQuotes from './quotes.reducer';

export interface QuotesState {
    quotes: fromQuotes.State;
    countries: fromCountries.CountriesState;
}

// extend root state
export interface State extends fromRoot.State {
    quotes: QuotesState;
}

export const reducers: ActionReducerMap<QuotesState> = {
    quotes: fromQuotes.reducer,
    countries: fromCountries.reducer
};

export const getQuotesState = createFeatureSelector<QuotesState>('quotes');

// quotes entity state
export const getEntitiesState = createSelector(getQuotesState, state => state.quotes);
export const getPreferredQuoteId = createSelector(getEntitiesState, fromQuotes.getPreferredQuoteId);
export const getSelectedQuoteId = createSelector(getEntitiesState, fromQuotes.getSelectedQuoteId);
export const getIsPending = createSelector(getEntitiesState, fromQuotes.getIsPending);
export const getPendingQuoteCollection = createSelector(getEntitiesState, fromQuotes.getPendingQuoteCollection);
export const getAddToQuoteNav = createSelector(getEntitiesState, fromQuotes.getAddToQuoteNav);
export const getAddToQuoteMenu = createSelector(getEntitiesState, fromQuotes.getAddToQuoteMenu);
export const getCreateNewQuote = createSelector(getEntitiesState, fromQuotes.getCreateNewQuote);
export const getChooseQuote = createSelector(getEntitiesState, fromQuotes.getChooseQuote);
export const getChosenQuote = createSelector(getEntitiesState, fromQuotes.getChosenQuote);
export const getNewlyCreatedQuote = createSelector(getEntitiesState, fromQuotes.getNewlyCreatedQuote);
export const getQuoteListMenu = createSelector(getEntitiesState, fromQuotes.getQuoteListMenu);
export const getQuoteEdit = createSelector(getEntitiesState, fromQuotes.getQuoteEdit);
export const getRequestQuote = createSelector(getEntitiesState, fromQuotes.getRequestQuote);
export const getQuoteDetail = createSelector(getEntitiesState, fromQuotes.getQuoteDetail);
export const getSelectedQuantity = createSelector(getEntitiesState, fromQuotes.getSelectedQuantity);
export const getQuoteOrderComplete = createSelector(getEntitiesState, fromQuotes.getQuoteOrderComplete);


export const {
    selectEntities: getQuoteEntities,
    selectAll: getQuotesArray
} = fromQuotes.adapter.getSelectors(getEntitiesState);

export const getPreferredQuote = createSelector(
    getQuoteEntities,
    getPreferredQuoteId,
    (quotes, preferredQuoteId: number) => {
        if (preferredQuoteId == null) {
            return null;
        }

        return quotes[preferredQuoteId];
    }
);

export const getSelectedQuoteBarItem = createSelector(
    getQuoteEntities,
    getChosenQuote,
    getPendingQuoteCollection,
    (quotes, selectedQuote, pendingQuoteCollection) => {
        if (selectedQuote == null) {
            return null;
        }

        return new QuoteBarItem(quotes[selectedQuote.id], pendingQuoteCollection[selectedQuote.id]);
    }
);

export const getNewlyCreatedQuoteBarItem = createSelector(
    getQuoteEntities,
    getNewlyCreatedQuote,
    getPendingQuoteCollection,
    (quotes, selectedQuote, pendingQuoteCollection) => {
        if (selectedQuote == null) {
            return null;
        }

        return new QuoteBarItem(quotes[selectedQuote.id], pendingQuoteCollection[selectedQuote.id]);
    }
);

export const getQuoteBarItemById = (id: number) => {
    return createSelector(
        getQuoteEntities,
        getPendingQuoteCollection,
        (quotes, pendingQuoteCollection) => {
            return new QuoteBarItem(quotes[id], pendingQuoteCollection[id]);
        }
    );
};

/**
 * Selector for an observable containing an array with all quote bar items.
 * @type {MemoizedSelector<object, QuoteBarItem[]>}
 */
export const getQuoteBarItems = createSelector(
    getQuotesArray,
    getPendingQuoteCollection,
    (quotes, pendingQuoteCollection) => {
        console.log(quotes)
        return quotes.map(q => new QuoteBarItem(q, pendingQuoteCollection[q.id]));
    }
);

/**
 * Selector for an observable containing an array with all quote bar items that have not been submitted.
 * @type {MemoizedSelector<object, QuoteBarItem[]>}
 */
export const getNewQuoteBarItems = createSelector(
    getQuoteBarItems,
    (quoteBarItems) => {
        return quoteBarItems.filter(q => q.quote.state === QuoteState.NEW);
    }
);

/**
 * Selector for an observable containing an array with all quote bar items that have been submitted.
 * @type {MemoizedSelector<object, QuoteBarItem[]>}
 */
export const getSubmittedQuoteBarItems = createSelector(
    getQuoteBarItems,
    (quoteBarItems) => {
        return quoteBarItems.filter(q => q.quote.state !== QuoteState.NEW);
    }
);

export const getHasNonEmptyQuotes = createSelector(
    getQuotesArray,
    (quotes) => {
        return quotes.filter(quote => quote.state === QuoteState.NEW)
            .filter(quote => quote.quoteItems.length > 0).length > 0;
    }
);

// countries entity state
export const getCountriesState = createSelector(getQuotesState, state => state.countries);
export const getCountriesIsPending = createSelector(getCountriesState, fromCountries.getIsPending);

export const {
    selectEntities: getCountryEntities,
    selectAll: getCountriesArray
} = fromCountries.adapter.getSelectors(getCountriesState);
